/**
 * M11: level of detail and spatial lookup for the galaxy map (pure functions, unit-tested).
 *
 *   galaxy  – the whole picture: clusters, regions, main routes; almost no text
 *   region  – stars, systems, fleets, ownership marks, names of the interesting systems
 *   close   – detailed stars, system marks, ships and routes, every name
 */
export type Lod = 'galaxy' | 'region' | 'close';

export const LOD_THRESHOLDS = { region: 0.45, close: 1.3, hysteresis: 0.06 } as const;

/** Level for a viewport scale; `prev` adds hysteresis so zooming around a threshold does not flicker. */
export function lodFor(scale: number, prev?: Lod): Lod {
  const { region, close, hysteresis: h } = LOD_THRESHOLDS;
  if (prev === 'close' && scale >= close * (1 - h)) return 'close';
  if (prev === 'region' && scale >= region * (1 - h) && scale < close * (1 + h)) return 'region';
  if (prev === 'galaxy' && scale < region * (1 + h)) return 'galaxy';
  return scale >= close ? 'close' : scale >= region ? 'region' : 'galaxy';
}

/** Multiplier applied to star glow size per level. */
export const LOD_STAR_MUL: Record<Lod, number> = { galaxy: 0.85, region: 1, close: 1.3 };
/** On-screen height (CSS px) of a fleet's ship marker per level. */
export const LOD_SHIP_PX: Record<Lod, number> = { galaxy: 16, region: 26, close: 40 };
/** Minimum on-screen diameter (CSS px) of a star's glow so that no star ever vanishes when zoomed out. */
export const MIN_STAR_GLOW_PX = 9;

export interface Rect { x0: number; y0: number; x1: number; y1: number }

export function inRect(x: number, y: number, r: Rect, margin = 0): boolean {
  return x >= r.x0 - margin && x <= r.x1 + margin && y >= r.y0 - margin && y <= r.y1 + margin;
}

/** The part of the world a viewport shows. */
export function visibleWorldRect(view: { x: number; y: number; scale: number }, screenW: number, screenH: number): Rect {
  return { x0: -view.x / view.scale, y0: -view.y / view.scale, x1: (screenW - view.x) / view.scale, y1: (screenH - view.y) / view.scale };
}

/** Uniform grid for "which system is under this tap" – O(1) instead of thousands of interactive display objects. */
export class SpatialGrid<T extends { x: number; y: number }> {
  private cells = new Map<string, T[]>();
  constructor(private readonly cell = 48) {}

  private key(ix: number, iy: number): string { return `${ix},${iy}`; }

  insert(item: T): void {
    const k = this.key(Math.floor(item.x / this.cell), Math.floor(item.y / this.cell));
    const list = this.cells.get(k);
    if (list) list.push(item); else this.cells.set(k, [item]);
  }

  clear(): void { this.cells.clear(); }

  /** Closest item within `maxDist` (world units) of the point, or null. */
  nearest(x: number, y: number, maxDist: number): T | null {
    const r = Math.ceil(maxDist / this.cell);
    const cx = Math.floor(x / this.cell), cy = Math.floor(y / this.cell);
    let best: T | null = null;
    let bestD = maxDist * maxDist;
    for (let ix = cx - r; ix <= cx + r; ix++) {
      for (let iy = cy - r; iy <= cy + r; iy++) {
        const list = this.cells.get(this.key(ix, iy));
        if (!list) continue;
        for (const it of list) {
          const d = (it.x - x) ** 2 + (it.y - y) ** 2;
          if (d <= bestD) { bestD = d; best = it; }
        }
      }
    }
    return best;
  }
}

/** Deterministic hash → [0, 1) for a grid cell (background nebulae / clusters never depend on game data). */
export function cellHash(ix: number, iy: number, salt = 0): number {
  let h = Math.imul(ix | 0, 0x27d4eb2d) ^ Math.imul(iy | 0, 0x165667b1) ^ Math.imul(salt | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Small deterministic PRNG for baked textures. */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
